import { useState, useRef, useCallback } from 'react';

interface SubmissionProtectionOptions {
  // Debounce time in milliseconds (default: 2000ms)
  debounceTime?: number;
  // Whether to hash the data for duplicate detection (default: true)
  enableDataHashing?: boolean;
  // Custom hash function (optional)
  customHashFn?: (data: any) => string;
}

export const useSubmissionProtection = (options: SubmissionProtectionOptions = {}) => {
  const {
    debounceTime = 2000,
    enableDataHashing = true,
    customHashFn
  } = options;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const lastSubmissionHashRef = useRef<string | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const createHash = useCallback((data: any): string => {
    if (customHashFn) {
      return customHashFn(data);
    }
    return JSON.stringify(data);
  }, [customHashFn]);

  const clearTimeoutRef = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const resetSubmission = useCallback(() => {
    setIsSubmitting(false);
    lastSubmissionHashRef.current = null;
    clearTimeoutRef();
  }, [clearTimeoutRef]);

  const protectedSubmit = useCallback(async <T>(
    data: T,
    submitFunction: (data: T) => Promise<any>
  ): Promise<any> => {
    // Prevent multiple submissions
    if (isSubmitting) {
      console.warn('Submission already in progress, ignoring duplicate submission');
      return;
    }

    // Check for duplicate data if enabled
    if (enableDataHashing) {
      const currentHash = createHash(data);
      
      if (lastSubmissionHashRef.current === currentHash) {
        console.warn('Duplicate submission detected, ignoring');
        return;
      }
      
      lastSubmissionHashRef.current = currentHash;
    }

    try {
      setIsSubmitting(true);
      const result = await submitFunction(data);
      
      // Set timeout to reset after debounce time
      timeoutRef.current = setTimeout(() => {
        lastSubmissionHashRef.current = null;
      }, debounceTime);
      
      return result;
    } catch (error) {
      // Reset immediately on error to allow retry
      lastSubmissionHashRef.current = null;
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  }, [isSubmitting, enableDataHashing, createHash, debounceTime]);

  return {
    isSubmitting,
    protectedSubmit,
    resetSubmission
  };
};

export default useSubmissionProtection;